/**
 * EditableInfrastructureLayer - Capa de infraestructuras con soporte de edición
 * V14-V16: Edición de coordenadas
 * 
 * Extiende InfrastructureLayer añadiendo capacidad de editar coordenadas
 * mediante arrastre de marcadores o click en el mapa.
 */

import React, { memo, useMemo, useCallback } from 'react';
import { useMapEvents } from 'react-leaflet';
import type { LeafletMouseEvent } from 'leaflet';
import type { NormalizedInfrastructure, InfrastructureType } from '../../../types/infrastructure';
import { MarkerCluster, type MarkerClusterOptions } from '../MarkerCluster';
import { InfrastructureMarker } from '../InfrastructureMarker';
import { EditableMarker } from './EditableMarker';
import { toUTM30 } from '../../../lib/leaflet-crs';
import type { PendingEdit } from './useEditMode';
import '../infrastructure.css';

// =============================================================================
// TIPOS
// =============================================================================

export interface EditableInfrastructureLayerProps {
  /** Array de infraestructuras normalizadas */
  data: NormalizedInfrastructure[];
  
  /** Si el modo edición está activo */
  editMode: boolean;
  
  /** Cambios pendientes (para mostrar coordenadas actualizadas) */
  pendingEdits: PendingEdit[];
  
  /** ID de infraestructura seleccionada para editar */
  selectedEditId?: string | null;
  
  /** Callback al arrastrar un marcador */
  onDragEnd?: (infra: NormalizedInfrastructure, newX: number, newY: number) => void;
  
  /** Callback al hacer click en el mapa (solo en modo edición) */
  onMapClick?: (x: number, y: number) => void;
  
  /** Callback al seleccionar infraestructura para editar */
  onSelectForEdit?: (infra: NormalizedInfrastructure) => void;
  
  // Props heredadas de InfrastructureLayer
  enableClustering?: boolean;
  clusterOptions?: Partial<MarkerClusterOptions>;
  typeFilter?: InfrastructureType[];
  minScore?: number;
  maxScore?: number;
  onInfrastructureClick?: (infrastructure: NormalizedInfrastructure) => void;
  onInfrastructureHover?: (infrastructure: NormalizedInfrastructure | null) => void;
  showPopups?: boolean;
  showTooltips?: boolean;
  selectedId?: string;
  popupOptions?: {
    showUTM?: boolean;
    showScore?: boolean;
    showSource?: boolean;
    showAddress?: boolean;
  };
}

// =============================================================================
// CONSTANTES
// =============================================================================

const DEFAULT_CLUSTER_OPTIONS: Partial<MarkerClusterOptions> = {
  maxClusterRadius: 60,
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  zoomToBoundsOnClick: true,
};

// =============================================================================
// UTILIDADES
// =============================================================================

function filterInfrastructures(
  data: NormalizedInfrastructure[],
  typeFilter?: InfrastructureType[],
  minScore?: number,
  maxScore?: number
): NormalizedInfrastructure[] {
  return data.filter((infra) => {
    if (typeFilter && typeFilter.length > 0 && !typeFilter.includes(infra.type)) {
      return false;
    }
    if (minScore !== undefined && infra.score < minScore) {
      return false;
    }
    if (maxScore !== undefined && infra.score > maxScore) {
      return false;
    }
    if (typeof infra.x !== 'number' || typeof infra.y !== 'number') {
      return false;
    }
    if (isNaN(infra.x) || isNaN(infra.y)) {
      return false;
    }
    return true;
  });
}

// =============================================================================
// SUBCOMPONENTE: Map Click Handler
// =============================================================================

interface MapClickHandlerProps {
  enabled: boolean;
  onClick: (x: number, y: number) => void;
}

const MapClickHandler = memo<MapClickHandlerProps>(function MapClickHandler({
  enabled,
  onClick,
}) {
  useMapEvents({
    click: (event: LeafletMouseEvent) => {
      if (!enabled) return;
      
      // Convertir lat/lng a UTM
      const [x, y] = toUTM30(event.latlng.lat, event.latlng.lng);
      onClick(x, y);
    },
  });
  
  return null;
});

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export const EditableInfrastructureLayer = memo<EditableInfrastructureLayerProps>(
  function EditableInfrastructureLayer({
    data,
    editMode,
    pendingEdits,
    selectedEditId,
    onDragEnd,
    onMapClick,
    onSelectForEdit,
    enableClustering = true,
    clusterOptions = {},
    typeFilter,
    minScore,
    maxScore,
    onInfrastructureClick,
    onInfrastructureHover,
    showPopups = true,
    showTooltips = true,
    selectedId,
    popupOptions = {},
  }) {
    // Filtrar infraestructuras
    const filteredData = useMemo(
      () => filterInfrastructures(data, typeFilter, minScore, maxScore),
      [data, typeFilter, minScore, maxScore]
    );
    
    // Mapa de ediciones pendientes por ID
    const editsMap = useMemo(() => {
      const map = new Map<string, PendingEdit>();
      for (const edit of pendingEdits) {
        map.set(edit.infrastructureId, edit);
      }
      return map;
    }, [pendingEdits]);
    
    // Opciones de clustering
    const mergedClusterOptions = useMemo<MarkerClusterOptions>(
      () => ({
        ...DEFAULT_CLUSTER_OPTIONS,
        ...clusterOptions,
      } as MarkerClusterOptions),
      [clusterOptions]
    );
    
    // Handler click en marcador
    const handleMarkerClick = useCallback(
      (infrastructure: NormalizedInfrastructure) => {
        if (editMode) {
          onSelectForEdit?.(infrastructure);
        } else {
          onInfrastructureClick?.(infrastructure);
        }
      },
      [editMode, onSelectForEdit, onInfrastructureClick]
    );
    
    // Handler drag end
    const handleDragEnd = useCallback(
      (infrastructure: NormalizedInfrastructure, newX: number, newY: number) => {
        onDragEnd?.(infrastructure, newX, newY);
      },
      [onDragEnd]
    );
    
    // Handler click en mapa
    const handleMapClick = useCallback(
      (x: number, y: number) => {
        onMapClick?.(x, y);
      },
      [onMapClick]
    );
    
    // Si no hay datos, no renderizar
    if (filteredData.length === 0) {
      return editMode ? (
        <MapClickHandler enabled={editMode} onClick={handleMapClick} />
      ) : null;
    }
    
    // Renderizar marcadores
    const renderMarkers = () => {
      return filteredData.map((infrastructure) => {
        const edit = editsMap.get(infrastructure.id);
        const hasChanges = !!edit;
        const currentCoords = edit ? edit.newCoords : undefined;
        const isSelectedForEdit = selectedEditId === infrastructure.id;
        
        // En modo edición, usar EditableMarker
        if (editMode) {
          return (
            <EditableMarker
              key={infrastructure.id}
              infrastructure={infrastructure}
              isEditable={true}
              isSelected={isSelectedForEdit}
              hasChanges={hasChanges}
              currentCoords={currentCoords}
              onClick={handleMarkerClick}
              onDragEnd={handleDragEnd}
            />
          );
        }
        
        // Modo normal: InfrastructureMarker estándar
        return (
          <InfrastructureMarker
            key={infrastructure.id}
            infrastructure={infrastructure}
            showPopup={showPopups}
            showTooltip={showTooltips}
            onClick={handleMarkerClick}
            onHover={onInfrastructureHover}
            selected={selectedId === infrastructure.id}
            popupOptions={popupOptions}
          />
        );
      });
    };
    
    // En modo edición, no usar clustering (para permitir arrastre libre)
    if (editMode) {
      return (
        <>
          <MapClickHandler enabled={editMode} onClick={handleMapClick} />
          {renderMarkers()}
        </>
      );
    }
    
    // Modo normal con clustering opcional
    if (!enableClustering) {
      return <>{renderMarkers()}</>;
    }
    
    return (
      <MarkerCluster options={mergedClusterOptions}>
        {renderMarkers()}
      </MarkerCluster>
    );
  }
);

export default EditableInfrastructureLayer;
